import React, { useContext, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Avatar, Badge, Layout, Dropdown } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  SettingOutlined,
  PoweroffOutlined,
  MenuOutlined,
  FolderOutlined,
  ToolOutlined,
  CommentOutlined,
  BookOutlined,
  DashboardOutlined,
  ProjectOutlined,
  ScheduleOutlined,
  HomeOutlined,
  FileTextOutlined,
  KeyOutlined,
  RobotOutlined,
  AuditOutlined,
  UserOutlined,
} from '@ant-design/icons';
import AuthContext from '../../contexts/authProvider';
import logo_icon from '../../images/logo_icon.svg';
import { INDEX_PATH, IS_PRODUCTION, IS_STAGING } from '../../utils/config';
import './header.scss';

const { Header } = Layout;

const AppHeader = () => {
  const { user, UnAuthUser } = useContext(AuthContext);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [activeMenu, setActiveMenu] = useState('');
  const [activeGroup, setActiveGroup] = useState('');

  const isOnSelectMenu = pathname === '/selectmenu';

  const goToHome = () => {
    navigate('/selectmenu');
  };

  const menuItems = useMemo(() => [
    {
      key: 'knowledgeAndTools',
      label: t('common.menu.knowledgeAndTools'),
      type: 'group',
      children: [
        // Hide FileManager in STAGING environment
        ...(IS_STAGING ? [] : [{
          key: 'fileSearch',
          label: t('common.menu.fileSearch'),
          icon: <FolderOutlined />,
          name: t('common.menu.fileSearch'),
          path: '/filemanager',
        }]),
        {
          key: 'askQuestion',
          label: t('common.menu.askQuestion'),
          icon: <CommentOutlined />,
          name: t('common.menu.askQuestion'),
          path: '/askquestion',
        },
        {
          key: 'knowledgeBaseManager',
          label: t('common.menu.knowledgeBaseManager'),
          icon: <BookOutlined />,
          name: t('common.menu.knowledgeBaseManager'), 
          path: '/knowledgeBaseManager',
        },
        {
          key: 'usefulTools',
          label: t('common.menu.usefulTools'),
          icon: <ToolOutlined />,
          name: t('common.menu.usefulTools'),
          path: '/usefulTools',
        },
        {
          key: 'documentParser',
          label: t('documentParser.menu'),
          icon: <FileTextOutlined />,
          name: t('documentParser.menu'),
          path: '/documentParser',
        },
      ],
    },
    {
      key: 'projectManagement',
      label: t('common.menu.projectManagement'),
      type: 'group',
      children: [
        {
          key: 'dashboard',
          label: t('common.menu.dashboard'),
          icon: <DashboardOutlined />,
          name: t('common.menu.dashboard'),
          path: '/project/dashboard',
        },
        {
          key: 'projectManage',
          label: t('common.menu.projectManage'),
          icon: <ProjectOutlined rotate={270} />,
          name: t('common.menu.projectManage'),
          path: '/project/management',
        },
        {
          key: 'projectPlanning',
          label: t('common.menu.projectPlanning'),
          icon: <ScheduleOutlined />,
          name: t('common.menu.projectPlanning'),
          path: '/project/planning',
        },
      ],
    },
    {
      key: 'settingsAndManagement',
      label: t('common.menu.settingsAndManagement'),
      type: 'group',
      children: [
        {
          key: 'personalTokens',
          label: t('common.menu.personalTokens'),
          icon: <KeyOutlined />,
          name: t('common.menu.personalTokens'),
          path: '/settings/tokens',
        },
        // System Admin-only menu items
        ...(user?.is_system_admin ? [
          {
            key: 'serviceAccounts',
            label: t('common.menu.serviceAccounts'),
            icon: <RobotOutlined />,
            name: t('common.menu.serviceAccounts'),
            path: '/admin/service-accounts',
          },
          {
            key: 'userTiers',
            label: t('common.menu.userTierManagement'),
            icon: <UserOutlined />,
            name: t('common.menu.userTierManagement'),
            path: '/admin/user-tiers',
          },
          {
            key: 'auditLogs',
            label: t('common.menu.auditLogs'),
            icon: <AuditOutlined />,
            name: t('common.menu.auditLogs'),
            path: '/admin/audit-logs',
          },
        ] : []),
      ],
    },
  ], [t, user]);

  const userItems = useMemo(
    () => [
      {
        key: 'home',
        label: <Link to="/selectmenu">{t('common.menu.home')}</Link>,
        icon: (
          <span onClick={() => navigate('/selectmenu')}>
            <HomeOutlined />
          </span>
        ),
        name: t('common.menu.home'),
        path: '/selectmenu',
      },
      {
        key: 'environment',
        label: <Link to="/settings">{t('common.menu.settings')}</Link>,
        icon: (
          <span onClick={() => navigate('/settings')}>
            <SettingOutlined />
          </span>
        ),
        name: t('common.menu.settings'),
        path: '/settings',
      },
      {
        type: 'divider',
      },
      {
        key: 'logout',
        label: <span onClick={() => UnAuthUser()}>{t('common.menu.logout')}</span>,
        icon: (
          <span onClick={() => UnAuthUser()}>
            <PoweroffOutlined />
          </span>
        ),
        name: t('common.menu.logout'),
        path: '',
      },
    ],
    [UnAuthUser, navigate, t]
  );

  React.useEffect(() => {
    // Don't set fileSearch as active in STAGING environment
    if (pathname === '/filemanager' && !IS_STAGING) {
      setActiveMenu('fileSearch');
      setActiveGroup('knowledgeAndTools');
    } else if (pathname.includes('/askquestion')) {
      setActiveMenu('askQuestion');
      setActiveGroup('knowledgeAndTools');
    } else if (pathname.includes('/knowledgeBaseManager')) {
      setActiveMenu('knowledgeBaseManager');
      setActiveGroup('knowledgeAndTools');
    } else if (pathname.includes('/usefulTools')) {
      setActiveMenu('usefulTools');
      setActiveGroup('knowledgeAndTools');
    } else if (pathname.includes('/documentParser')) {
      setActiveMenu('documentParser');
      setActiveGroup('knowledgeAndTools');
    } else if (pathname.includes('/project/dashboard')) {
      setActiveMenu('dashboard');
      setActiveGroup('projectManagement');
    } else if (pathname.includes('/project/management')) {
      setActiveMenu('projectManage');
      setActiveGroup('projectManagement');
    } else if (pathname.includes('/project/planning')) {
      setActiveMenu('projectPlanning');
      setActiveGroup('projectManagement');
    } else if (pathname.includes('/settings/tokens')) {
      setActiveMenu('personalTokens');
      setActiveGroup('settingsAndManagement');
    } else if (pathname.includes('/admin/service-accounts')) {
      setActiveMenu('serviceAccounts');
      setActiveGroup('settingsAndManagement');
    } else if (pathname.includes('/admin/user-tiers')) {
      setActiveMenu('userTiers');
      setActiveGroup('settingsAndManagement');
    } else if (pathname.includes('/admin/audit-logs')) {
      setActiveMenu('auditLogs');
      setActiveGroup('settingsAndManagement');
    } else if (pathname === '/selectmenu') {
      setActiveMenu('');
      setActiveGroup('');
    }
  }, [pathname]);

  const getCurrentPageTitle = () => {
    if (pathname === INDEX_PATH) return t('common.menu.fileSearch');
    // Don't show FileManager title in STAGING environment
    if (pathname === '/filemanager' && !IS_STAGING) return t('common.menu.fileSearch');
    if (pathname.includes('/askquestion')) return t('common.menu.askQuestion');
    if (pathname.includes('/knowledgeBaseManager')) return t('common.menu.knowledgeBaseManager');
    if (pathname.includes('/usefulTools')) return t('common.menu.usefulTools');
    if (pathname.includes('/documentParser')) return t('documentParser.title');
    if (pathname === '/settings') return t('common.menu.settings');
    if (pathname.includes('/project/dashboard')) return t('common.menu.dashboard');
    if (pathname.includes('/project/management')) return t('common.menu.projectManage');
    if (pathname.includes('/project/planning')) return t('common.menu.projectPlanning');
    if (pathname.includes('/settings/tokens')) return t('common.menu.personalTokens');
    if (pathname.includes('/admin/service-accounts')) return t('common.menu.serviceAccounts');
    if (pathname.includes('/admin/user-tiers')) return t('common.menu.userTierManagement');
    if (pathname.includes('/admin/audit-logs')) return t('common.menu.auditLogs');
    return t('common.menu.fileSearch');
  };

  return (
    <Header className="app-header">
      <div className="app-header-container">
        <div className="app-header-left">
          <div className="app-header-logo" onClick={goToHome}>
            <img src={logo_icon} alt={t('common.title')} />
            <div className="app-header-titles">
              <span className="app-header-main-title">{t('common.title')}</span>
              {!isOnSelectMenu && (
                <span className="app-header-page-title">{getCurrentPageTitle()}</span>
              )}
            </div>
          </div>

          <nav className="app-header-nav">
            <div className={`app-header-nav-item ${activeGroup === 'knowledgeAndTools' ? 'active' : ''}`}>
              <span>{t('common.menu.knowledgeAndTools')}</span>
              <div className="app-header-nav-item-group">
                <div className="app-header-nav-item-group-items">
                  {menuItems[0].children.map(item => (
                    <div 
                      className={`app-header-nav-item-group-item ${activeMenu === item.key ? 'active' : ''}`} 
                      key={item.key}
                    >
                      <Link to={item.path}>
                        {item.icon}
                        {item.label}
                      </Link>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            {/* WIP: PRODUCTION AND STAGING ENVIRONMENT */}
            <div className={`app-header-nav-item ${activeGroup === 'projectManagement' ? 'active' : ''}`}
              style={{ display: (IS_PRODUCTION || IS_STAGING) ? 'none' : '' }}
            >
              <span>{t('common.menu.projectManagement')}</span>
              <div className="app-header-nav-item-group">
                <div className="app-header-nav-item-group-items">
                  {menuItems[1].children.map(item => (
                    <div
                      className={`app-header-nav-item-group-item ${activeMenu === item.key ? 'active' : ''}`}
                      key={item.key}
                    >
                      <Link to={item.path}>
                        {item.icon}
                        {item.label}
                      </Link>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            {/* Settings and Management */}
            <div className={`app-header-nav-item ${activeGroup === 'settingsAndManagement' ? 'active' : ''}`}>
              <span>{t('common.menu.settingsAndManagement')}</span>
              <div className="app-header-nav-item-group">
                <div className="app-header-nav-item-group-items">
                  {menuItems[2].children.map(item => (
                    <div
                      className={`app-header-nav-item-group-item ${activeMenu === item.key ? 'active' : ''}`}
                      key={item.key}
                    >
                      <Link to={item.path}>
                        {item.icon}
                        {item.label}
                      </Link>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </nav>
        </div>

        <ul className="app-header-right">
          <li className="app-header-mobile-menu">
            {/* WIP: PRODUCTION AND STAGING ENVIRONMENT */}
            <Dropdown
              placement="bottomRight"
              menu={{
                items: menuItems
                  .filter(group => !((IS_PRODUCTION || IS_STAGING) && group.key === 'projectManagement'))
                  .map(group => ({
                    key: group.key,
                    label: group.label,
                    type: 'group',
                    children: group.children.map(item => ({
                      key: item.key,
                      label: <Link to={item.path}>{item.label}</Link>,
                      icon: item.icon,
                      className: activeMenu === item.key ? 'active-dropdown-item' : ''
                    })),
                  })),
              }}
              trigger={['click']}
            >
              <MenuOutlined />
            </Dropdown>
          </li>
          <li className="app-header-user">
            <div className="user">
              <Dropdown placement="bottomRight" menu={{ items: userItems }} trigger={['click']}>
                <div>
                  <Badge size="small" count={0}>
                    <Avatar size="small" src={user?.picture} />
                  </Badge>
                  <span className="name">{user?.given_name}</span>
                </div>
              </Dropdown>
            </div>
          </li>
        </ul>
      </div>
    </Header>
  );
};

export default AppHeader;
